/**
 * Sub-controller for Test Drive Flyout — Choose Models (Preferred Model multi-select).
 *
 * Owns checkbox selection with a maximum of three Preferred Models and FIFO eviction when a
 * fourth is chosen. Isolated from the legacy configurator test drive surface.
 */
export const testDriveChooseModels = {
  /**
   * Bootstrap the Choose Models view. Idempotent; safe to call once at boot.
   */
  testDriveChooseModelsView() {
    this.helpers.bootstrap();
  },

  /**
   * Controller / state container. Skipped by `processInclude` (not a function).
   */
  helpers: {
    /**
     * Maximum Preferred Models the shopper may select.
     *
     * @private
     * @readonly
     */
    MAX_PREFERRED_MODELS: 3,

    /**
     * Preferred Model product ids in insertion order (oldest first). Source of truth for FIFO
     * eviction — not inferred from DOM order.
     *
     * @private
     *
     * @type {string[]}
     */
    selectionOrder: [],

    /**
     * Latest variant image URL for the currently configured model, captured from
     * `product:afterAttributeSelect`. Cached because the event can fire before the flyout is
     * wired (e.g. `initDefaultVariation()` on page load).
     *
     * @private
     *
     * @type {string}
     */
    latestVariantImage: '',

    /**
     * @private
     *
     * @type {boolean}
     */
    isWired: false,

    /**
     * @private
     *
     * @type {{
     *   view: HTMLElement | null,
     *   list: HTMLElement | null,
     *   nextBtn: HTMLButtonElement | null,
     *   countEl: HTMLElement | null
     * }}
     */
    elements: {
      view: null,
      list: null,
      nextBtn: null,
      countEl: null
    },

    /**
     * Lazy-wire on first show of the Choose Models view. Also tracks variant changes so the
     * preselected model card can mirror the color chosen on the configurator.
     */
    bootstrap() {
      $('body').on('product:afterAttributeSelect', (event, payload) => this.onAttributeSelect(payload));

      document.body.addEventListener('amb:modal-view-changed', event => {
        if (event.detail?.view !== 'test-drive') {
          return;
        }

        if (!this.wire()) {
          return;
        }

        this.seedSelectionOrder();
        this.applySelectedColorImage();
        this.syncUI();
      });
    },

    /**
     * Cache the freshest variant image for the configured model and reflect it on the card.
     * The event fires on every attribute change (color/grade) via SFRA `base.js`, so no extra
     * request is needed here.
     *
     * @param {{ data?: { product?: { images?: { large?: Array<{ url: string }> } } } }} [payload]
     *
     * @private
     */
    onAttributeSelect(payload) {
      const url = payload?.data?.product?.images?.large?.[0]?.url;

      if (!url) {
        return;
      }

      this.latestVariantImage = url;
      this.applySelectedColorImage();
    },

    /**
     * Swap the preselected (currently configured) model card image to the selected color variant.
     * No-op until the list is wired and a variant image has been captured.
     *
     * @private
     */
    applySelectedColorImage() {
      const list = this.elements.list;

      if (!list || !this.latestVariantImage) {
        return;
      }

      const currentId = list.dataset.currentModel;

      if (!currentId) {
        return;
      }

      const input = list.querySelector(`.model--input[data-product-id="${currentId}"]`);
      const image = input?.closest('.model-card')?.querySelector('.model--image');

      if (!(image instanceof HTMLImageElement)) {
        return;
      }

      const base = this.latestVariantImage;

      image.src = base + (base.indexOf('?') === -1 ? '?' : '&') + 'width=548&fit=scale-down&transform=true';
    },

    /**
     * Resolve element refs and attach listeners. Selection is seeded on every view enter.
     *
     * @returns {boolean} `true` once wiring has succeeded.
     */
    wire() {
      if (this.isWired) {
        return true;
      }

      const view = document.querySelector('.configurator-modal .modal-view[view="test-drive"]');

      if (!view) {
        return false;
      }

      const list = view.querySelector('.test-drive-model-list');

      if (!list) {
        return false;
      }

      this.elements.view = view;
      this.elements.list = list;
      this.elements.nextBtn = view.querySelector('.btn-test-drive-next');
      this.elements.countEl = view.querySelector('.test-drive-choose-models__count');

      list.addEventListener('change', event => this.onPreferredModelChange(event));

      this.isWired = true;

      return true;
    },

    /**
     * Rebuild {@link selectionOrder} from the checkboxes that are actually checked.
     *
     * The default (configured) vehicle is applied only when nothing is selected — e.g. a cold
     * open. Returning to this view after another page keeps checked cards, so those win.
     *
     * @private
     */
    seedSelectionOrder() {
      const list = this.elements.list;
      const checkedIds = [];

      list.querySelectorAll('.model--input:checked').forEach(input => {
        const productId = input.dataset.productId;

        if (productId && !checkedIds.includes(productId)) {
          checkedIds.push(productId);
        }
      });

      if (!checkedIds.length) {
        const currentId = list.dataset.currentModel;
        const currentInput = currentId
          ? list.querySelector(`.model--input[data-product-id="${currentId}"]`)
          : null;

        if (currentInput) {
          currentInput.checked = true;
          this.selectionOrder = [currentId];
        } else {
          this.selectionOrder = [];
        }

        return;
      }

      const stillSelected = this.selectionOrder.filter(id => checkedIds.includes(id));
      const newcomers = checkedIds.filter(id => !stillSelected.includes(id));

      this.selectionOrder = stillSelected.concat(newcomers).slice(0, this.MAX_PREFERRED_MODELS);

      list.querySelectorAll('.model--input:checked').forEach(input => {
        if (!this.selectionOrder.includes(input.dataset.productId)) {
          input.checked = false;
        }
      });
    },

    /**
     * Checkbox `change` — toggle Preferred Model membership and apply FIFO eviction at the max.
     *
     * @param {Event} event
     *
     * @private
     */
    onPreferredModelChange(event) {
      const input = event.target.closest('.model--input');

      if (!input || !this.elements.list.contains(input)) {
        return;
      }

      const productId = input.dataset.productId;

      if (!productId) {
        return;
      }

      if (input.checked) {
        this.addPreferredModel(productId);
      } else {
        this.removePreferredModel(productId);
      }

      this.syncUI();
    },

    /**
     * Add a Preferred Model, evicting the oldest by insertion order when already at the maximum.
     *
     * @param {string} productId
     *
     * @private
     */
    addPreferredModel(productId) {
      if (this.selectionOrder.includes(productId)) {
        return;
      }

      if (this.selectionOrder.length >= this.MAX_PREFERRED_MODELS) {
        const oldestId = this.selectionOrder.shift();
        const oldestInput = this.elements.list.querySelector(`.model--input[data-product-id="${oldestId}"]`);

        if (oldestInput) {
          oldestInput.checked = false;
        }
      }

      this.selectionOrder.push(productId);
    },

    /**
     * Remove a Preferred Model from insertion order.
     *
     * @param {string} productId
     *
     * @private
     */
    removePreferredModel(productId) {
      const index = this.selectionOrder.indexOf(productId);

      if (index === -1) {
        return;
      }

      this.selectionOrder.splice(index, 1);
    },

    /**
     * Refresh the selection counter and Next button enabled state.
     *
     * @private
     */
    syncUI() {
      const count = this.selectionOrder.length;
      const countEl = this.elements.countEl;
      const max = Number(countEl?.dataset.max) || this.MAX_PREFERRED_MODELS;
      const fractionEl = countEl?.querySelector('.test-drive-choose-models__fraction');

      if (fractionEl) {
        fractionEl.textContent = `${count}/${max}`;
      }

      this.elements.nextBtn?.toggleAttribute('disabled', count === 0);
    }
  }
};
